import { useEffect, useState, useCallback, type FormEvent } from 'react';
import {
  Box, Button, Container, CssBaseline, Grid, Typography, Paper,
  IconButton, Alert
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';

const LOGO_STORAGE_KEY = 'app_logo_media';

export default function MediaManagement() {
  const { tokens } = useAuth();
  const [medias, setMedias] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoMedia, setLogoMedia] = useState<any>(() => {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem(LOGO_STORAGE_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      window.localStorage.removeItem(LOGO_STORAGE_KEY);
      return null;
    }
  });
/*  const [editOpen, setEditOpen] = useState(false);
  const [editMedia, setEditMedia] = useState<any>(null);
  const [editFile, setEditFile] = useState<File | null>(null);*/

  const updateLogoCache = useCallback((logo: any | null) => {
    if (typeof window === 'undefined') return;
    if (logo) {
      window.localStorage.setItem(LOGO_STORAGE_KEY, JSON.stringify(logo));
    } else {
      window.localStorage.removeItem(LOGO_STORAGE_KEY);
    }
    if (typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('app-logo-updated', { detail: logo }));
    }
  }, []);

  const fetchMedias = useCallback(async () => {
    try {
      const res = await fetch(`${config.apiUrl}/api/media/all`, {
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      });
      const data = await res.json();
      setMedias(data.content || []);
      const logo = (data.content || []).find((m: any) => m.category === 'logo');
      setLogoMedia(logo || null);
      updateLogoCache(logo || null);
    } catch (e) {
      console.error(e);
      setError('Failed to fetch media.');
    }
  }, [tokens?.accessToken, updateLogoCache]);

  useEffect(() => {
    if (!tokens?.accessToken) {
      setMedias([]);
      setLogoMedia(null);
      return;
    }
    fetchMedias();
  }, [fetchMedias, tokens?.accessToken]);

  // 🔹 Helper: Datei -> Base64
  async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // 🔹 Create Logo
  async function handleLogoUpload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!logoFile) return;
    setError('');
    setSuccess('');

    const base64 = await fileToBase64(logoFile);
    const payload = {
      title: 'logo',
      category: 'logo',
      image: base64
    };

    try {
      const res = await fetch(`${config.apiUrl}/api/media/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.content) {
        setSuccess('Logo uploaded successfully.');
        setLogoFile(null);
        fetchMedias();
      } else {
        setError('Logo upload failed.');
      }
    } catch (err) {
      console.error(err);
      setError('Logo upload failed.');
    }
  }

  // 🔹 Upload Gallery
  async function handleUploadSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!uploadFile) return;
    setError('');
    setSuccess('');
    const base64 = await fileToBase64(uploadFile);
    const timestamp = Date.now();

    const payload = {
      title: `gallery_${timestamp}`,
      category: 'gallery',
      image: base64
    };

    try {
      const res = await fetch(`${config.apiUrl}/api/media/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.content) {
        setSuccess('Media uploaded successfully.');
        setUploadFile(null);
        fetchMedias();
      } else {
        setError('Upload failed.');
      }
    } catch {
      setError('Upload failed.');
    }
  }

  async function handleDelete(id: string) {
    try {
      const res = await fetch(`${config.apiUrl}/api/media/delete/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      });
      if (res.ok) {
        setSuccess('Media deleted.');
        fetchMedias();
      } else setError('Delete failed.');
    } catch {
      setError('Delete failed.');
    }
  }

  return (
      <Container component="main" maxWidth="md">
        <CssBaseline />
        <Box sx={{ mt: 4 }}>
          <Typography variant="h4" gutterBottom>Media Management</Typography>

          {/* Logo Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>Logo</Typography>
            {!logoMedia ? (
                <form onSubmit={handleLogoUpload}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Button variant="contained" component="label" fullWidth>
                        Select Logo Image
                        <input type="file" accept="image/*" hidden onChange={e => setLogoFile(e.target.files?.[0] || null)} />
                      </Button>
                      {logoFile && <Typography sx={{ mt: 1 }}>{logoFile.name}</Typography>}
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" color="primary">Create Logo</Button>
                    </Grid>
                  </Grid>
                </form>
            ) : (
                <Box textAlign="center">
                  <img src={logoMedia.image} alt="Logo" style={{ maxWidth: '200px' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button variant="contained" color="error" onClick={() => handleDelete(logoMedia.id)}>
                      Delete Logo
                    </Button>
                  </Box>
                </Box>
            )}
          </Paper>

          {/* Gallery Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <form onSubmit={handleUploadSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button variant="contained" component="label" fullWidth>
                    Upload Image
                    <input type="file" accept="image/*" hidden onChange={e => setUploadFile(e.target.files?.[0] || null)} />
                  </Button>
                  {uploadFile && <Typography sx={{ mt: 1 }}>{uploadFile.name}</Typography>}
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" color="primary">Save Media</Button>
                </Grid>
              </Grid>
            </form>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
          </Paper>

          <Typography variant="h5" gutterBottom>Saved Images</Typography>
          <Grid container spacing={2}>
            {medias
            .filter((m: any) => m.category === 'gallery')
            .map((m: any) => (
                <Grid item xs={12} sm={6} md={4} key={m.id}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <img src={m.image} alt={m.title} style={{ maxWidth: '100%', maxHeight: 180 }} />
                    <Typography>{m.title}</Typography>
                    <IconButton color="error" onClick={() => handleDelete(m.id)}><Delete /></IconButton>
                  </Paper>
                </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
  );
}
